import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import { Box, IconButton, Tooltip, useTheme } from '@mui/material';
import React from 'react';
import { ThemePreference, usePreferredTheme } from '../hooks/usePreferredTheme';

interface ThemeIconSelectorProps {
    /**
     * Optional callback when theme preference changes
     */
    onChange?: (preference: ThemePreference) => void;
    /**
     * Size of the icons
     */
    size?: 'small' | 'medium' | 'large';
}

/**
 * Icon-based theme selector for AppBar usage
 * Shows three icons: Light, Dark, and Automatic with visual feedback for active selection
 * 
 * Features:
 * - Sun icon (LightModeIcon) for light theme
 * - Moon icon (DarkModeIcon) for dark theme  
 * - Settings icon (SettingsBrightnessIcon) for automatic/system preference
 * - Active selection highlighted with border
 * - Tooltips showing current state
 * - Smooth hover transitions
 */
export const ThemeIconSelector: React.FC<ThemeIconSelectorProps> = ({
    onChange,
    size = 'medium'
}) => {
    const theme = useTheme();
    const { themePreference, setThemePreference, actualMode } = usePreferredTheme();

    const handleThemeChange = (preference: ThemePreference) => {
        setThemePreference(preference);
        onChange?.(preference);
    };

    const getIconOpacity = (preference: ThemePreference): number => {
        return themePreference === preference ? 1 : 0.6;
    };

    const getIconSx = (preference: ThemePreference) => {
        const isActive = themePreference === preference;
        return {
            opacity: getIconOpacity(preference),
            borderRadius: 1,
            border: isActive ? '2px solid currentColor' : '2px solid transparent',
            color: theme.palette.text.navbar, // Use same color as menu text
            '&:hover': {
                opacity: 1,
                backgroundColor: theme.palette.action.hover
            }
        };
    };

    const getTooltipText = (preference: ThemePreference): string => {
        const baseText = {
            light: 'Ljust tema',
            dark: 'Mörkt tema',
            automatic: 'Automatiskt (systeminställning)'
        }[preference];

        const isActive = themePreference === preference;
        return isActive ? `${baseText} (aktiv)` : baseText;
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 2 }}>
            <Tooltip title={getTooltipText('light')}>
                <IconButton
                    size={size}
                    color="default"
                    onClick={() => handleThemeChange('light')}
                    sx={getIconSx('light')}
                    aria-label="Ljust tema"
                >
                    <LightModeIcon />
                </IconButton>
            </Tooltip>

            <Tooltip title={getTooltipText('dark')}>
                <IconButton
                    size={size}
                    color="default"
                    onClick={() => handleThemeChange('dark')}
                    sx={getIconSx('dark')}
                    aria-label="Mörkt tema"
                >
                    <DarkModeIcon />
                </IconButton>
            </Tooltip>

            <Tooltip title={getTooltipText('automatic')}>
                <IconButton
                    size={size}
                    color="default"
                    onClick={() => handleThemeChange('automatic')}
                    sx={getIconSx('automatic')}
                    aria-label="Automatiskt tema"
                >
                    <SettingsBrightnessIcon />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

export default ThemeIconSelector;
